import type {
  AdminSnapshot,
  AgentProfile,
  AgentSession,
  ChatEvent,
  FileReadPayload,
  FileSearchPayload,
  FileWritePayload,
  HealthPayload,
  PersistedTerminal,
  ProjectRecord,
  RecentFolderRecord,
  TerminalMeta,
  WorkbenchContainer,
  WorkbenchSnapshot,
} from "../types";
import { clerkFetch } from "./clerkFetch";

export interface SettingsSnapshot {
  agents: Record<string, string>;
  appearance: { theme: "dark" | "light" | "system" };
  notifications: {
    enabled: boolean;
    triggers: { "response-ready": boolean; "cli-idle": boolean; "approval-needed": boolean };
    chime: boolean;
  };
}

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export interface AgentModeOption {
  id: string;
  label: string;
  description: string;
  unsafe: boolean;
}
export type AgentModesCatalog = Record<string, AgentModeOption[]>;

export type AgentUpdateStatus = "up-to-date" | "available" | "not-installed" | "error";

export interface AgentUpdateEntry {
  installed: string | null;
  latest: string | null;
  status: AgentUpdateStatus;
}

export interface AgentUpdatesSnapshot {
  lastCheckAt: string | null;
  snapshot: Record<string, AgentUpdateEntry>;
}

export type AgentUpdateEvent =
  | { type: "log"; chunk: string; stream: "stdout" | "stderr"; ts: number }
  | { type: "status"; state: "running" | "success" | "failure"; exitCode?: number }
  | { type: "done"; installed: string | null }
  | { type: "error"; message: string };

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

// The ApiError message for a response body that isn't JSON — which on the hosted
// web app is the normal case for any /api/* path, because no Bun host runs there
// and Cloudflare's SPA fallback answers with index.html and a 200.
//
// Exported so tests assert against this definition instead of retyping the
// literal; a copy in a test file stays green after an edit here, which is how a
// message-dependent behaviour breaks silently. (No runtime narrowing keys on it:
// isHostUnavailable in AdminConsoleView deliberately keys on STATUS, since
// client.ts parses the body before checking response.ok and so raises this
// message for HTML-bodied error responses too.)
export const MALFORMED_RESPONSE_MESSAGE = "Malformed response from server";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  // All API calls route through clerkFetch so the Clerk JWT is attached and
  // 401/402 are handled centrally.
  const response = await clerkFetch(path, {
    credentials: "same-origin",
    ...options,
    headers,
  });
  const text = await response.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiError(response.status, MALFORMED_RESPONSE_MESSAGE, text);
  }
  if (!response.ok) {
    if (response.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("agentguard:auth-required", {
          detail: {
            path,
            adminConfigured: Boolean((data as { adminConfigured?: unknown })?.adminConfigured),
          },
        })
      );
    }
    const errorField = (data as { error?: unknown })?.error;
    const message = typeof errorField === "string" ? errorField : `HTTP ${response.status}`;
    throw new ApiError(response.status, message, data);
  }
  return data as T;
}

export type ScanLocalRiskResult =
  | {
      ok: true;
      base: string;
      branch: string;
      prUrl: string;
      changedFiles: string[];
      diff: string;
      truncated: boolean;
      excludedRestricted: number;
    }
  | { ok: false; reason: string; excludedRestricted?: number };

// Remediation "Fix to 100" (Wave 2e) — mirrors the host buildReport output.
export interface RemediationReport {
  outcome: {
    cleanAchieved: boolean;
    terminationReason: string;
    finalHealth: number | null;
    // Failure reason for a setup-failed/run-error outcome (e.g. uncommitted
    // changes, bad base, missing repo). Optional for back-compat with older
    // persisted records.
    error?: string | null;
  };
  progression: Array<{
    round: number;
    healthBefore: number;
    healthAfter: number;
    delta: number;
    fixedIds: string[];
    introducedIds: string[];
    fixAttempted: boolean;
    verifyPassed: boolean;
  }>;
  summary: {
    startHealth: number | null;
    finalHealth: number | null;
    totalRounds: number;
    fixedIds: string[];
    fixedCount: number;
    remainingIds: string[];
    remainingCount: number;
  };
  cost: {
    rounds: number;
    sends: number;
    elapsedMs: number;
    tokensIn: number;
    tokensOut: number;
    cents: number | null;
  } | null;
  gatedActions: Array<{
    id: string;
    command: string;
    pack: string;
    rule: string;
    reason: string;
    decision: string;
  }>;
  branch: string | null;
}

export interface RemediationRunStatus {
  state: "running" | "done" | "error";
  round?: number;
  health?: number | null;
  report?: RemediationReport;
  markdown?: string;
  branch?: string | null;
  error?: string;
}

// Backend enhance-review run (Review pane "Fix to 100"). The 6-dimension
// rubric shape matches convex/review/score.ts.
export interface ReviewRubric {
  codeQuality: number;
  testCoverage: number;
  documentation: number;
  design: number;
  security: number;
  ci: number;
  overall: number;
}

export interface ReviewRunApproval {
  id: string;
  command: string;
  pack: string;
  rule: string;
  reason: string;
  decision: "auto-allowed" | "held" | string;
}

// Steer-to-Safety batch (review every open PR, sequentially).
export interface ReviewBatchItem {
  repo: string;
  prNumber: number;
  title?: string;
  state: "queued" | "running" | "done" | "error" | "skipped";
  runId: string | null;
  reason?: string | null;
  rubricFinal?: ReviewRubric | null;
  commentUrl?: string | null;
  pushed?: boolean;
}

export interface ReviewBatchStatus {
  state: "running" | "done";
  batchId: string;
  items: ReviewBatchItem[];
  error?: string | null;
}

export interface ReviewRunActivity {
  kind: "tool";
  label: string;
  detail?: string;
  ts: number;
}

export interface ReviewRunStatus {
  state: "running" | "done" | "error";
  phase?: "setup" | "agent";
  runId: string;
  repo: string;
  prNumber: number;
  prUrl: string;
  baseline: ReviewRubric | null;
  approvals: ReviewRunApproval[];
  // Live sneak-peek: rolling tool-call feed + the tail of the agent's
  // streamed text (display-only).
  activity?: ReviewRunActivity[];
  textPreview?: string;
  lastEventAt?: number;
  branch?: string | null;
  chatId?: string | null;
  rubricFinal?: ReviewRubric | null;
  commentUrl?: string | null;
  pushed?: boolean;
  timedOut?: boolean;
  error?: string | null;
  // Set when this record was neither created by the viewer nor host-attested.
  // The pane attributes it and stops linking its caller-supplied commentUrl —
  // a forged run could otherwise present an attacker-chosen URL as a verified
  // review. See convex/review/queries.ts latestForPr.
  unverified?: boolean;
  createdBy?: string;
}

export const api = {
  health: () => request<HealthPayload>("/api/health"),
  // Spec 5.1 PR3: getWorkbench/createSession/appendSessionMessage removed
  // (routes are 410'd). React reads via convex:api.workbench.queries.snapshot
  // and writes via convex:api.sessions.mutations.{create,archive}.
  //
  // Spec 5.1 PR 6: commandCheck removed — WorkbenchView now calls
  // useConvex().query(api.dlp.queries.commandCheck) directly.
  //
  // Spec 5.1 PR 5 — decideApproval + createSecurityException migrated to
  // Convex. The REST routes return 410. React callers now use:
  //   approvals.resolve     → useMutation(api.approvals.mutations.resolve)
  //   create exception      → useMutation(api.policy.mutations.createException)
  getProjects: () =>
    request<{ projects: ProjectRecord[]; recentFolders: RecentFolderRecord[] }>("/api/projects"),
  createProject: (body: { name?: string; rootPath: string }) =>
    request<ProjectRecord>("/api/projects", { method: "POST", body: JSON.stringify(body) }),
  deleteProject: (id: string) =>
    request<{ ok: true }>(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE" }),
  setProjectDevServerUrl: (projectId: string, url: string) =>
    request<{ id: string; devServerUrl: string | null }>(
      `/api/projects/${encodeURIComponent(projectId)}/dev-server-url`,
      {
        method: "PUT",
        body: JSON.stringify({ url }),
      }
    ),
  getFileTree: (params: { projectId?: string; rootPath?: string; path?: string }) => {
    const query = new URLSearchParams();
    if (params.projectId) query.set("projectId", params.projectId);
    if (params.rootPath) query.set("rootPath", params.rootPath);
    if (params.path) query.set("path", params.path);
    return request<{ root: string; entries: Array<Record<string, unknown>> }>(
      `/api/fs/tree?${query.toString()}`
    );
  },
  approveRestrictedRead: (params: {
    projectId?: string | null;
    rootPath?: string | null;
    path: string;
  }) =>
    request<{ restrictedToken: string; path: string; reason?: string; expiresIn?: number }>(
      "/api/fs/approve-read",
      {
        method: "POST",
        body: JSON.stringify(params),
      }
    ),
  readFile: (params: {
    projectId?: string | null;
    rootPath?: string | null;
    path: string;
    allowRestricted?: boolean;
    restrictedToken?: string;
  }) => {
    const query = new URLSearchParams();
    if (params.projectId) query.set("projectId", params.projectId);
    if (params.rootPath) query.set("rootPath", params.rootPath);
    query.set("path", params.path);
    if (params.allowRestricted) query.set("allowRestricted", "1");
    if (params.restrictedToken) query.set("restrictedToken", params.restrictedToken);
    return request<FileReadPayload>(`/api/fs/read?${query.toString()}`);
  },
  resolveFile: (params: { projectId?: string | null; rootPath?: string | null; path: string }) => {
    const query = new URLSearchParams();
    if (params.projectId) query.set("projectId", params.projectId);
    if (params.rootPath) query.set("rootPath", params.rootPath);
    query.set("path", params.path);
    return request<{
      resolved: string | null;
      restricted?: boolean;
      restrictedReason?: string | null;
    }>(`/api/fs/resolve?${query.toString()}`);
  },
  writeFile: (body: {
    projectId?: string | null;
    rootPath?: string | null;
    path: string;
    content: string;
    approved?: boolean;
    approvalToken?: string;
  }) => request<FileWritePayload>("/api/fs/write", { method: "POST", body: JSON.stringify(body) }),
  searchFiles: (params: {
    projectId?: string | null;
    rootPath?: string | null;
    type: "files" | "text";
    q: string;
    max?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.projectId) query.set("projectId", params.projectId);
    if (params.rootPath) query.set("rootPath", params.rootPath);
    query.set("type", params.type);
    query.set("q", params.q);
    if (params.max) query.set("max", String(params.max));
    return request<FileSearchPayload>(`/api/fs/search?${query.toString()}`);
  },
  validateFolder: (rootPath: string) =>
    request<RecentFolderRecord>("/api/folders/validate", {
      method: "POST",
      body: JSON.stringify({ rootPath }),
    }),
  // PR-risk (Wave 1e): gather the active workspace's local git diff on the
  // host so the frontend can feed it to the Convex risk.actions.analyzePr
  // action. Returns a discriminated union — `ok:false` carries a reason the
  // card maps to a friendly inline message.
  scanLocalRisk: (params: { projectId?: string; rootPath?: string }) =>
    request<ScanLocalRiskResult>("/api/risk/scan-local", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  // Remediation "Fix to 100" (Wave 2e). Start a run, poll its live status, list
  // recent runs. All gated server-side (clerkAuth + requirePlan('security')).
  startRemediationRun: (params: {
    rootPath?: string;
    projectId?: string;
    workspaceDir?: string;
    base?: string;
    testCommand?: string;
  }) =>
    request<{ runId: string; chatId: string }>("/api/remediation/runs", {
      method: "POST",
      body: JSON.stringify(params),
    }),
  getRemediationRunStatus: (runId: string) =>
    request<RemediationRunStatus>(`/api/remediation/runs/${encodeURIComponent(runId)}`),
  // Backend enhance-review (Review pane). Start a headless review run on the
  // selected GitHub PR, then poll its live status (held approvals included).
  // Gated server-side (clerkAuth + the pro plan).
  startReviewRun: (params: {
    rootPath?: string;
    projectId?: string;
    workspaceDir?: string;
    repo: string;
    prNumber: number;
    headRef?: string;
    prUrl?: string;
    baseline?: ReviewRubric | null;
    agent?: string;
  }) =>
    request<{ runId: string }>("/api/review/runs", {
      method: "POST",
      body: JSON.stringify(params),
    }),
  getReviewRunStatus: (runId: string) =>
    request<ReviewRunStatus>(`/api/review/runs/${encodeURIComponent(runId)}`),
  // Re-attach lookup: the active (or just-finished) run for a PR, so a
  // reloaded view re-binds its run panel instead of orphaning the run.
  getActiveReviewRun: (params: { repo: string; prNumber: number }) =>
    request<{ runId: string | null; state?: "running" | "done" | "error" }>(
      `/api/review/runs/active?repo=${encodeURIComponent(params.repo)}&prNumber=${params.prNumber}`
    ),
  // Steer to Safety: review & fix EVERY open PR sequentially (one agent at a
  // time). Returns the batchId; poll the batch status for per-PR outcomes.
  startReviewAll: (params: {
    rootPath?: string;
    projectId?: string;
    workspaceDir?: string;
    agent?: string;
  }) =>
    request<{ batchId: string }>("/api/review/runs/all", {
      method: "POST",
      body: JSON.stringify(params),
    }),
  getReviewBatchStatus: (batchId: string) =>
    request<ReviewBatchStatus>(`/api/review/runs/batch/${encodeURIComponent(batchId)}`),
  // Decide a HOST-held (JsonStore `appr-*`) approval — the review run's held
  // risky actions resolve here, not via the Convex approvals mutation (their
  // rows live in the host securityLoop store). For a Convex approval id a
  // terminal is holding its PTY on, the route records the decision in Convex
  // as the caller, then releases the terminal and answers `bridged: true`;
  // for any other Convex id it answers 410. The route requires a note when
  // denying.
  decideHostApproval: (id: string, decision: "approved" | "denied", note?: string) =>
    request<{ ok: boolean; bridged?: boolean }>(
      `/api/approvals/${encodeURIComponent(id)}/decision`,
      {
        method: "POST",
        body: JSON.stringify({
          decision,
          note: note || (decision === "denied" ? "Denied from Review pane" : ""),
        }),
      }
    ),
  listRemediationRuns: (limit?: number) =>
    request<{
      runs: Array<{
        runId: string;
        branch: string | null;
        createdAt: number;
        // Declared REQUIRED so a CONSUMER cannot quietly stop reading them —
        // verified: making either optional fails the build at
        // RemediationHistory.tsx. It does NOT guard the producer:
        // src/remediation/reportBuilder.js is untyped Bun-host JS outside this
        // tsconfig, so deleting the lines that emit them keeps tsc green. That
        // side is pinned by an assertion in
        // test/remediation/reportBuilder.test.js instead, because it is exactly
        // how these came to render nowhere the first time.
        createdBy: string | null;
        provenance: "host" | "user" | null;
        attestable?: boolean;
        report: RemediationReport;
      }>;
    }>(`/api/remediation/runs${limit ? `?limit=${limit}` : ""}`),
  getWorkbenches: async () => {
    const data = await request<{ workbenches: WorkbenchContainer[] }>("/api/workbenches");
    return data.workbenches;
  },
  createWorkbench: async (name: string) =>
    request<WorkbenchContainer>("/api/workbenches", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  updateWorkbench: (id: string, body: { name?: string; emoji?: string; layout?: string }) =>
    request<WorkbenchContainer>(`/api/workbenches/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteWorkbench: (id: string) =>
    request<{ ok: true }>(`/api/workbenches/${encodeURIComponent(id)}`, { method: "DELETE" }),
  getAgents: async () => {
    const data = await request<{ agents: AgentProfile[] }>("/api/agents");
    return data.agents;
  },
  createAgent: (body: {
    name: string;
    cmd: string;
    args?: string[];
    env?: Record<string, string>;
  }) => request<AgentProfile>("/api/agents", { method: "POST", body: JSON.stringify(body) }),
  deleteAgent: (id: string) =>
    request<{ ok: true }>(`/api/agents/${encodeURIComponent(id)}`, { method: "DELETE" }),
  getTerminals: async () => {
    const data = await request<{ terminals: TerminalMeta[] }>("/api/terminals");
    return data.terminals;
  },
  spawnTerminal: (body: {
    agent: string;
    sessionId?: string | null;
    workbenchId?: string;
    projectId?: string;
    rootPath?: string;
    cwd?: string;
    cols?: number;
    rows?: number;
    reattachId?: string;
    // The persisted record a resume respawns under a new id (src/server.js).
    restoreOf?: string;
  }) => request<TerminalMeta>("/api/terminals", { method: "POST", body: JSON.stringify(body) }),
  deleteTerminal: (id: string) =>
    request<{ ok: true }>(`/api/terminals/${encodeURIComponent(id)}`, { method: "DELETE" }),
  listPersistedTerminals: async (): Promise<PersistedTerminal[]> => {
    const data = await request<{ persistedTerminals: PersistedTerminal[] }>(
      "/api/persisted-terminals"
    );
    return data.persistedTerminals;
  },
  dismissPersistedTerminal: (id: string) =>
    request<{ ok: true }>(`/api/persisted-terminals/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  // Returns the last `tail` bytes of the persisted transcript with metadata
  // prefixes already stripped by the server. Plain text — not JSON — so we
  // can't reuse request<T>() and have to fetch directly.
  fetchTerminalTranscript: async (id: string, tail = 524288) => {
    const response = await clerkFetch(
      `/api/persisted-terminals/${encodeURIComponent(id)}/transcript?tail=${tail}`,
      { credentials: "same-origin" }
    );
    if (!response.ok)
      throw new ApiError(
        response.status,
        `Failed to read transcript: HTTP ${response.status}`,
        null
      );
    return response.text();
  },
  // Spec 5.1 PR 5 — /api/security-room migrated to convex:
  //   useQuery(api.security_room.queries.snapshot) on the React side.
  // Spec 5.1 PR 2 — MIGRATED to Convex hooks. Helpers below either
  // call Convex via api.policy.*, api.firewall.*, api.risk.* or were
  // removed entirely. The corresponding REST routes return 410 with
  // Cache-Control:no-store + a migrated_to hint.
  // Removed helpers (consumed by AdminConsoleView / SecurityRoom):
  //   classifyFirewallCommand → useConvex().query(api.firewall.queries.testCommand)
  //   listPolicyPacks         → useQuery(api.policy.queries.packs)
  //   upsertPolicyPack        → useAction(api.policy.actions.parseAndStorePack)
  //   deletePolicyPack        → useMutation(api.policy.mutations.deletePack)
  //   simulatePolicyEvent     → (deferred to PR3 — api.policy.queries.simulate)
  //   getEffectivePolicies    → useQuery(api.policy.queries.effective)
  //   reloadPolicyEngine      → useMutation(api.policy.mutations.reload)
  //   getRepoLabels           → useQuery(api.policy.queries.effective).repoLabels
  //   setRepoLabels           → useMutation(api.policy.mutations.setEnforcementMode)
  //   getFirewallPolicies     → useQuery(api.firewall.queries.listRules)
  //   createFirewallPolicy    → useMutation(api.firewall.mutations.upsertRule)
  //   deleteFirewallPolicy    → useMutation(api.firewall.mutations.removeRule)
  //   getPrPolicies           → useQuery(api.policy.queries.prPolicies)
  //   getAgentPolicies        → useQuery(api.policy.queries.agentPolicies)
  //   updateAgentPolicy       → useMutation(api.policy.mutations.setAgentOverride)
  //
  // getEnforcement stays here — it returns the rich host-side tier data
  // (PATH shims / DYLD / EndpointSecurity / LLM tap install state) that
  // lives on the Bun host per spec §5 bucket-B. The thinner
  // {mode, shimsInstalled, tapInstalled} shape is available via
  // useQuery(api.policy.queries.effective).enforcement for callers that
  // don't need the full Tier panel.
  getEnforcement: () => request<Record<string, unknown>>("/api/admin/enforcement"),
  setShimEnforcement: (enabled: boolean) =>
    request<Record<string, unknown>>(
      `/api/admin/enforcement/shims/${enabled ? "install" : "uninstall"}`,
      { method: "POST" }
    ),
  setTapEnforcement: (enabled: boolean) =>
    request<Record<string, unknown>>(
      `/api/admin/enforcement/tap/${enabled ? "install" : "uninstall"}`,
      { method: "POST" }
    ),
  // THROWS on failure — deliberately. This used to wrap the whole body in a
  // try/catch that returned `{ authorized: false, error }` for every failure,
  // which erased the one thing the caller needs: the HTTP status. On
  // app.steerly.ai there is no Bun host, so Cloudflare's SPA fallback answers
  // /api/admin/whoami with 200 text/html, JSON.parse throws, and the swallow
  // turned "nothing is listening here" into "you are not authorized" — the red
  // banner that gated off every Convex-backed pane, GitHub integration
  // included, for exactly the security-plan users who run this probe at all.
  //
  // AdminConsoleView.refresh() already classifies the failure correctly
  // (classifyHostFailure: 401/402/403 = a verdict on the caller, 2xx = no host
  // behind this origin, anything else = a host that answered badly). That fix
  // shipped in #277 and was unreachable, because the throw never got out of
  // here. One classifier, one place, and the status survives the trip.
  getAdminSnapshot: async (): Promise<AdminSnapshot> => {
    // Spec 5.1 PR 2 — policy/firewall/pr-risk/agents reads moved
    // to Convex hooks (api.policy.*, api.firewall.*, api.risk.*).
    // AdminConsoleView calls useQuery for those panes directly.
    // getAdminSnapshot here keeps audit (PR 4 migration) and
    // enforcement (rich host-side tier data: PATH shims, DYLD,
    // EndpointSecurity daemon, LLM tap — bucket-B per spec §5).
    await request<{ ok: boolean }>("/api/admin/whoami");
    const [audit, enforcement] = await Promise.all([
      request<{ events: AdminSnapshot["auditEvents"] }>("/api/admin/audit?limit=200"),
      // Kept tolerant: the enforcement tier detail is a nice-to-have panel, not
      // a reason to declare the whole console unavailable.
      api.getEnforcement().catch((error) => ({
        error: error instanceof Error ? error.message : String(error),
      })),
    ]);
    return {
      authorized: true,
      packs: [],
      auditEvents: audit.events,
      enforcement,
    };
  },
  getSettings: () => request<SettingsSnapshot>("/api/settings"),
  updateSettings: (patch: DeepPartial<SettingsSnapshot>) =>
    request<SettingsSnapshot>("/api/settings", { method: "PUT", body: JSON.stringify(patch) }),
  setAgentMode: (agentId: string, modeId: string) =>
    request<SettingsSnapshot>(`/api/settings/agents/${encodeURIComponent(agentId)}`, {
      method: "PUT",
      body: JSON.stringify({ mode: modeId }),
    }),
  getAgentModes: () => request<AgentModesCatalog>("/api/agent-modes"),

  getAgentUpdates: () => request<AgentUpdatesSnapshot>("/api/agent-updates"),

  checkAgentUpdates: (force = false) =>
    request<AgentUpdatesSnapshot>("/api/agent-updates/check", {
      method: "POST",
      body: JSON.stringify({ force }),
    }),

  installAgentUpdate(
    id: string,
    onEvent: (e: AgentUpdateEvent) => void,
    onDone: () => void,
    onError: (err: Error) => void
  ): () => void {
    const controller = new AbortController();
    (async () => {
      try {
        // clerkFetch (not raw fetch) so the Clerk Bearer token is attached —
        // the route is clerkAuth + requireBaseOrSecurity, and the SPA's session
        // cookie isn't reliably present cross-origin / in the Tauri webview, so
        // a raw fetch 401s. Mirrors chatApi.sendMessage's streaming POST.
        const res = await clerkFetch(`/api/agent-updates/install/${encodeURIComponent(id)}`, {
          method: "POST",
          credentials: "same-origin",
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`Install failed: ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n\n")) >= 0) {
            const frame = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const lines = frame.split("\n");
            const dataLine = lines.find((l) => l.startsWith("data: "));
            if (!dataLine) continue;
            try {
              onEvent(JSON.parse(dataLine.slice(6)) as AgentUpdateEvent);
            } catch {
              /* ignore parse errors on heartbeat lines */
            }
          }
        }
        onDone();
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") onError(err);
      }
    })();
    return () => controller.abort();
  },
};

export function terminalWebSocketUrl(id: string, convexToken?: string | null): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const base = `${protocol}//${window.location.host}/api/terminals/${encodeURIComponent(id)}/io`;
  // Spec 7 entitlement gate (server.js terminal upgrade) reads the org's tier
  // from Convex, which requires the Clerk "convex"-template JWT. A browser
  // WebSocket can't set an Authorization / X-Convex-Token header, so we pass
  // the token as a `ct` query param. It's a short-lived JWT over wss; the host
  // falls back to the session cookie (and fails closed) when it's absent.
  return convexToken ? `${base}?ct=${encodeURIComponent(convexToken)}` : base;
}

export type ActivityStreamMode = "sse" | "poll" | "error";

export interface ActivityStreamHandle {
  close: () => void;
  mode: ActivityStreamMode;
}

// Subscribe to admin activity events. Prefers the SSE stream (live tail);
// transparently degrades to 5s polling on connect error so the Activity tab
// still updates if the eventsource path is blocked by a proxy.
//
// Implemented over fetch() instead of EventSource: the stream route is
// Clerk-auth'd AND plan-gated, and requirePlan resolves entitlements via the
// Clerk "convex"-template JWT (X-Convex-Token header). EventSource cannot
// set request headers, so on a real (non-self-host) deployment the gate only
// saw the session cookie, Convex rejected its audience, entitlements failed
// closed to [] and every subscribed org got a 402 + a permanently
// "Reconnecting…" Security Room stream. clerkFetch attaches Authorization +
// X-Convex-Token — the same headers every other gated route uses.
export function subscribeAdminActivity(
  onAppend: (event: Record<string, unknown>) => void,
  onModeChange?: (mode: ActivityStreamMode) => void
): ActivityStreamHandle {
  let mode: ActivityStreamMode = "sse";
  const abort = new AbortController();
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let lastSeenId: string | null = null;
  let closed = false;

  function handleFrame(frame: string): void {
    // Frames arrive as `event: append\ndata: {json}`; heartbeats are `: hb…`
    // comment lines and the handshake frame is `event: connected`.
    let eventName = "message";
    const dataLines: string[] = [];
    for (const rawLine of frame.split("\n")) {
      const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
      if (line.startsWith("event:")) eventName = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }
    if (eventName !== "append" || dataLines.length === 0) return;
    try {
      const parsed = JSON.parse(dataLines.join("\n"));
      const item =
        parsed && typeof parsed === "object" && "event" in parsed
          ? (parsed as { event: Record<string, unknown> }).event
          : parsed;
      onAppend(item as Record<string, unknown>);
      if (item && typeof (item as Record<string, unknown>).id === "string") {
        lastSeenId = (item as Record<string, unknown>).id as string;
      }
    } catch {
      // ignore malformed frames
    }
  }

  function onStreamFailure(): void {
    // Emit an "error" signal BEFORE switching to polling. This lets the caller
    // (the SecurityRoomView reconnect loop) decide whether to retry SSE with
    // backoff. Polling fallback continues to run so the view still gets data
    // while the caller schedules a reconnect attempt.
    if (closed) return;
    if (mode === "sse") {
      mode = "error";
      onModeChange?.(mode);
      // If the caller closed us during the error signal — typically because
      // SecurityRoomView's reconnect loop calls handle.close() before retry —
      // do NOT start the polling fallback. The setInterval below would
      // otherwise be created AFTER `closed = true` was set, leaking past
      // the close() call. The new connect() will spin up its own handle.
      if (closed) return;
      mode = "poll";
      onModeChange?.(mode);
      // Exponential backoff on consecutive failures.
      //
      // A flat 5s retry with a bare `catch {}` meant a host that rejects every
      // request — e.g. one booted without CLERK_JWT_KEY, which 401s all of
      // /api/* — was hammered twice a minute forever, in every open tab, with
      // the only evidence a wall of identical console warnings. A poll that
      // never gives up and never reports is indistinguishable from one that is
      // working. Successes reset the interval immediately.
      const BASE_POLL_MS = 5000;
      const MAX_POLL_MS = 60_000;
      let consecutiveFailures = 0;

      const schedule = (delay: number) => {
        // `closed` is checked HERE rather than at each call site. The failure
        // path checked it and the success path did not, so a request still in
        // flight when the caller unsubscribed would re-enter schedule() and
        // create a fresh interval — a zombie poller calling onAppend on an
        // unmounted component forever. One guard at the top is the version that
        // cannot be half-applied.
        if (closed) return;
        pollTimer = setInterval(async () => {
          if (closed) {
            if (pollTimer) clearInterval(pollTimer);
            return;
          }
          try {
            const data = await request<{ events: Array<Record<string, unknown>> }>(
              "/api/admin/audit?limit=50"
            );
            if (closed) return;
            for (const item of data.events) {
              if (lastSeenId && item.id === lastSeenId) break;
              onAppend(item);
            }
            if (data.events[0]?.id) lastSeenId = String(data.events[0].id);
            if (consecutiveFailures > 0) {
              consecutiveFailures = 0;
              if (pollTimer) clearInterval(pollTimer);
              schedule(BASE_POLL_MS);
            }
          } catch {
            consecutiveFailures += 1;
            const next = Math.min(BASE_POLL_MS * 2 ** consecutiveFailures, MAX_POLL_MS);
            if (next !== delay) {
              if (pollTimer) clearInterval(pollTimer);
              schedule(next);
            }
          }
        }, delay);
      };
      schedule(BASE_POLL_MS);
    }
  }

  void (async () => {
    try {
      const res = await clerkFetch("/api/admin/activity/stream", {
        headers: { Accept: "text/event-stream" },
        credentials: "same-origin",
        signal: abort.signal,
      });
      if (!res.ok || !res.body) throw new Error(`activity stream ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.search(/\r?\n\r?\n/)) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep).replace(/^\r?\n\r?\n/, "");
          handleFrame(frame);
        }
      }
      // Server ended the stream — surface it like an EventSource error so
      // the caller's reconnect loop takes over.
      onStreamFailure();
    } catch {
      onStreamFailure();
    }
  })();

  return {
    get mode() {
      return mode;
    },
    close: () => {
      closed = true;
      abort.abort();
      if (pollTimer) clearInterval(pollTimer);
    },
  } as ActivityStreamHandle;
}

// Spec 5.1 PR 6 — chats CRUD (create/list/get/delete/rename) migrated to
// Convex. The REST routes return 410. React callers now use:
//   chats.create     → useMutation(api.chats.mutations.create)
//   chats.list       → useQuery(api.chats.queries.list)
//   chats.byId       → useQuery(api.chats.queries.byId)
//   chats.delete     → useMutation(api.chats.mutations.deleteChat)
//   chats.title      → useMutation(api.chats.mutations.title)
// sendMessage stays — /api/chats/:id/messages is bucket B per spec §5.5
// (real-time LLM token stream via chatEngine).
export const chatApi = {
  sendMessage(
    id: string,
    content: string,
    onEvent: (e: ChatEvent) => void,
    onError: (err: Error) => void
  ): () => void {
    // EventSource doesn't support POST; use fetch + ReadableStream + manual SSE parse.
    const controller = new AbortController();
    (async () => {
      try {
        const res = await clerkFetch(`/api/chats/${encodeURIComponent(id)}/messages`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ content }),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`Failed to send: ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n\n")) >= 0) {
            const frame = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const lines = frame.split("\n");
            const eventLine = lines.find((l) => l.startsWith("event: "));
            const dataLine = lines.find((l) => l.startsWith("data: "));
            if (!eventLine || !dataLine) continue;
            const eventType = eventLine.slice(7);
            if (eventType === "chat-event") {
              try {
                const payload = JSON.parse(dataLine.slice(6)) as ChatEvent;
                onEvent(payload);
              } catch {
                /* ignore */
              }
            } else if (eventType === "error") {
              try {
                const payload = JSON.parse(dataLine.slice(6)) as { message?: string };
                onError(new Error(payload.message || "stream error"));
              } catch {
                onError(new Error("stream error"));
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") onError(err);
      }
    })();
    return () => controller.abort();
  },
};

// Streams the NDJSON via clerkFetch (so the Bearer token is attached) and
// triggers a browser download from the resulting Blob. The previous
// window.open(url) approach issued an unauthenticated GET and the server
// returned 401. Honours the same `kind` filter the on-screen list uses, so
// the export matches what the operator sees.
export async function downloadAuditNdjson(params: {
  since?: string;
  until?: string;
  kind?: string;
}): Promise<void> {
  const q = new URLSearchParams({ format: "ndjson" });
  if (params.since) q.set("since", params.since);
  if (params.until) q.set("until", params.until);
  if (params.kind && params.kind !== "all") q.set("type", params.kind);
  const res = await clerkFetch(`/api/admin/audit?${q}`);
  if (!res.ok) throw new Error(`Audit export failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `audit-${new Date().toISOString()}.ndjson`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Free the blob URL after the click has been dispatched.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

// Spec 5.1 PR 2 — fetchPRRiskBriefs / testPolicy / fetchPolicyPacks /
// updatePolicyPack / createPolicyPack were REMOVED. Replacements:
//   fetchPRRiskBriefs  → useQuery(api.risk.queries.recentBriefs)
//   testPolicy         → useConvex().query(api.firewall.queries.testCommand)
//   fetchPolicyPacks   → useQuery(api.policy.queries.packs)
//   updatePolicyPack   → useAction(api.policy.actions.parseAndStorePack)
//   createPolicyPack   → useAction(api.policy.actions.parseAndStorePack)
// The corresponding REST routes return 410 Gone with Cache-Control:no-store
// and a migrated_to hint. See PolicyPacksPage, PRRiskPage, PolicyTester,
// AdminConsoleView for the new Convex callsites.

// fetchMemoryGraph + createMemoryNote were REMOVED in Spec 5.1 PR 1.
// Replacements:
//   GET  /api/memory/graph → useQuery(api.memory.notesQueries.notesForGraph)
//                            or useQuery(api.memory.queries.readGraph)
//   POST /api/memory/notes → useMutation(api.memory.notesMutations.appendNote)
// The Node routes now return 410 with a migration hint and no auth.
